/**
 * PauseMenuPanel - Menu shown when game is paused (ESC during gameplay)
 */

import { UIPanel } from './UIPanel';
import { Vector2 } from '../math/Vector2';
import { t, Localization } from '../systems/Localization';
import { renderLogo, getLogoDimensions } from './Logo';

export class PauseMenuPanel extends UIPanel {
  private hoveredButton: string | null = null;

  // Callbacks
  public onResume: (() => void) | null = null;
  public onSave: (() => void) | null = null;
  public onSaveAs: (() => void) | null = null;
  public onLoad: (() => void) | null = null;
  public onNewGame: (() => void) | null = null;

  private buttons: { id: string; label: string; y: number }[] = [];

  constructor() {
    super(0, 0, 300, 440);
    this.isDraggable = false;
    this.backgroundColor = 'rgba(20, 20, 40, 0.95)';
    this.updateButtons();
  }

  private updateButtons(): void {
    this.title = t('menu_paused');
    const logoHeight = getLogoDimensions(3).height;
    const startY = this.bounds.y + this.titleBarHeight + logoHeight + 40;
    const buttonSpacing = 50;

    this.buttons = [
      { id: 'resume', label: t('menu_resume').toUpperCase(), y: startY },
      { id: 'save', label: t('menu_save_game').toUpperCase(), y: startY + buttonSpacing },
      { id: 'saveas', label: t('menu_save_as').toUpperCase(), y: startY + buttonSpacing * 2 },
      { id: 'load', label: t('menu_load_game').toUpperCase(), y: startY + buttonSpacing * 3 },
      { id: 'newgame', label: t('menu_new_game').toUpperCase(), y: startY + buttonSpacing * 4 },
      { id: 'language', label: `${t('menu_language')}: ${Localization.getLanguageName()}`, y: startY + buttonSpacing * 5 }
    ];
  }

  show(): void {
    super.show();
    this.updateButtons();
  }

  centerOnScreen(screenWidth: number, screenHeight: number): void {
    super.centerOnScreen(screenWidth, screenHeight);
    this.updateButtons();
  }

  protected onPanelClick(pos: Vector2): boolean {
    for (const button of this.buttons) {
      const buttonX = this.bounds.x + 40;
      const buttonWidth = this.bounds.width - 80;
      const buttonHeight = 40;

      if (pos.x >= buttonX && pos.x <= buttonX + buttonWidth &&
          pos.y >= button.y && pos.y <= button.y + buttonHeight) {
        switch (button.id) {
          case 'resume':
            this.onResume?.();
            break;
          case 'save':
            this.onSave?.();
            break;
          case 'saveas':
            this.onSaveAs?.();
            break;
          case 'load':
            this.onLoad?.();
            break;
          case 'newgame':
            this.onNewGame?.();
            break;
          case 'language':
            Localization.toggleLanguage();
            this.updateButtons();
            break;
        }
        return true;
      }
    }
    return true;
  }

  handleMouseMove(pos: Vector2): boolean {
    if (super.handleMouseMove(pos)) return true;

    if (!this.getIsVisible() || !this.containsPoint(pos)) {
      this.hoveredButton = null;
      return false;
    }

    this.hoveredButton = null;
    for (const button of this.buttons) {
      const buttonX = this.bounds.x + 40;
      const buttonWidth = this.bounds.width - 80;
      const buttonHeight = 40;

      if (pos.x >= buttonX && pos.x <= buttonX + buttonWidth &&
          pos.y >= button.y && pos.y <= button.y + buttonHeight) {
        this.hoveredButton = button.id;
        break;
      }
    }

    return true;
  }

  protected renderContent(ctx: CanvasRenderingContext2D): void {
    // Render logo at top
    const logoY = this.bounds.y + this.titleBarHeight + 12;
    renderLogo(ctx, this.bounds.x + this.bounds.width / 2, logoY, 3, '#4ecca3');

    // Render buttons
    for (const button of this.buttons) {
      this.renderButton(ctx, button);
    }
  }

  private renderButton(
    ctx: CanvasRenderingContext2D,
    button: { id: string; label: string; y: number }
  ): void {
    const buttonX = this.bounds.x + 40;
    const buttonWidth = this.bounds.width - 80;
    const buttonHeight = 40;
    const isHovered = this.hoveredButton === button.id;

    // Button background colors
    let bgColor = isHovered ? '#666' : '#555';
    if (button.id === 'resume') {
      bgColor = isHovered ? '#5dde80' : '#4ecca3';
    } else if (button.id === 'save') {
      bgColor = isHovered ? '#4aa3d8' : '#3498db';
    } else if (button.id === 'newgame') {
      bgColor = isHovered ? '#e67e22' : '#d35400';
    } else if (button.id === 'language') {
      bgColor = isHovered ? '#9b59b6' : '#8e44ad';
    }

    ctx.fillStyle = bgColor;
    ctx.fillRect(buttonX, button.y, buttonWidth, buttonHeight);

    // Button border
    ctx.strokeStyle = isHovered ? '#fff' : '#888';
    ctx.lineWidth = 2;
    ctx.strokeRect(buttonX, button.y, buttonWidth, buttonHeight);

    // Button text
    ctx.fillStyle = '#fff';
    ctx.font = 'bold 14px monospace';
    ctx.textAlign = 'center';
    ctx.fillText(button.label, buttonX + buttonWidth / 2, button.y + 26);
  }

  // Override to prevent closing with X button
  protected isCloseButtonClick(_pos: Vector2): boolean {
    return false;
  }

  // Don't render close button
  protected renderCloseButton(_ctx: CanvasRenderingContext2D): void {
    // No close button for pause menu
  }
}
